import React, { useRef, useState } from 'react';
import { VegaFlex, VegaCard, VegaButton, VegaInput, VegaForm } from '@heartlandone/vega-react';
import { VegaNotify } from '@heartlandone/vega';
import logo from 'Public/Image/Logo.svg';
import { copyToClipboard } from 'Chores/utils';
import { requestResetCode, resetPassword } from './Services';
import './PasswordReset.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_COOLDOWN_MINUTES = 10;

const toPositiveMinutes = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  return Math.ceil(minutes);
};

const getMinutesFromNextAllowedAt = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const nextAllowedAt = Date.parse(value);
  if (Number.isNaN(nextAllowedAt)) {
    return null;
  }
  const deltaMinutes = Math.ceil((nextAllowedAt - Date.now()) / 60000);
  return deltaMinutes > 0 ? deltaMinutes : null;
};

const extractCooldownMinutes = (result) => {
  const directCandidates = [
    result?.cooldownMinutes,
    result?.CooldownMinutes,
    result?.cooldownMinute,
    result?.CooldownMinute,
    result?.cooldown,
    result?.Cooldown
  ];

  for (const candidate of directCandidates) {
    const parsed = toPositiveMinutes(candidate);
    if (parsed) {
      return parsed;
    }
  }

  const nextAllowedAtCandidates = [
    result?.nextRequestAllowedAt,
    result?.NextRequestAllowedAt,
    result?.cooldownUntil,
    result?.CooldownUntil
  ];

  for (const candidate of nextAllowedAtCandidates) {
    const parsed = getMinutesFromNextAllowedAt(candidate);
    if (parsed) {
      return parsed;
    }
  }

  return DEFAULT_COOLDOWN_MINUTES;
};

const emailValidationRule = {
  overrideDefault: true,
  canEvaluate(input) {
    return typeof input === 'string';
  },
  evaluate: (input) => {
    if (!input.trim()) {
      return { isValid: false, message: 'Email is required.' };
    }
    if (!EMAIL_REGEX.test(input.trim().toLowerCase())) {
      return { isValid: false, message: 'Please enter a valid email address.' };
    }
    return { isValid: true, message: '' };
  }
};

const codeValidationRule = {
  overrideDefault: true,
  canEvaluate(input) {
    return typeof input === 'string';
  },
  evaluate: (input) => {
    if (!input.trim()) {
      return { isValid: false, message: 'Verification code is required.' };
    }
    return { isValid: true, message: '' };
  }
};

const PasswordReset = () => {
  const requestFormRef = useRef();
  const resetFormRef = useRef();

  const [requestLoading, setRequestLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [tempPassword, setTempPassword] = useState(null);
  const [cooldownMinutes, setCooldownMinutes] = useState(DEFAULT_COOLDOWN_MINUTES);

  const handleCopyTempPassword = () => {
    if (!tempPassword) return;
    copyToClipboard(tempPassword, 'Temporary password copied to clipboard.');
  };

  // ── Step 1: Request verification code ──
  const handleRequestCode = async () => {
    const res = await requestFormRef.current.valid();
    if (!res.isValid) return;

    const values = await requestFormRef.current.getValue();
    const email = values.email.trim().toLowerCase();

    setRequestLoading(true);
    try {
      const result = await requestResetCode(email);
      setRequestEmail(email);
      setCodeSent(true);
      setCooldownMinutes(extractCooldownMinutes(result));
      VegaNotify.open({
        type: 'success',
        message: result?.message || 'A verification code has been sent to your email.'
      });
    } catch (err) {
      VegaNotify.open({
        type: 'error',
        message: err.message || 'Failed to request verification code.'
      });
    } finally {
      setRequestLoading(false);
    }
  };

  const cooldownLabel = `${cooldownMinutes} minute${cooldownMinutes === 1 ? '' : 's'}`;

  // ── Step 2: Reset password with code ──
  const handleReset = async () => {
    const res = await resetFormRef.current.valid();
    if (!res.isValid) return;

    const values = await resetFormRef.current.getValue();
    const email = values.resetEmail.trim().toLowerCase();
    const code = values.code.trim();

    setResetLoading(true);
    try {
      const result = await resetPassword(email, code);
      setTempPassword(result.temporaryPassword);
      VegaNotify.open({
        type: 'success',
        message: result?.message || 'Password has been reset successfully.'
      });
    } catch (err) {
      VegaNotify.open({
        type: 'error',
        message: err.message || 'Failed to reset password.'
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="password-reset-page">
      <VegaFlex direction="col" alignItems="center" gap="size-32">
        <img src={logo} alt="Logo" className="password-reset-logo" />

        <h1 className="v-font-h3">Password Reset</h1>

        {/* ── Instructions ── */}
        <VegaCard className="password-reset-card">
          <div className="password-reset-instructions">
            <p><strong>How it works:</strong></p>
            <ol>
              <li>Enter your registered email address and click <strong>Send Code</strong>.</li>
              <li>Check your inbox for a verification code (valid for <strong>10 minutes</strong>).</li>
              <li>Enter your email and the code below, then click <strong>Reset Password</strong>.</li>
              <li>You will receive a <strong>temporary password</strong> on this page. Use it to sign in — you will be required to set a new password on first login.</li>
            </ol>
            <p className="password-reset-note">
              <strong>Note:</strong> You can only request one reset every <strong>{cooldownLabel}</strong> per email address. If you already requested a code, wait for the cooldown to expire before trying again.
            </p>
          </div>
        </VegaCard>

        {/* ── Request Code Section ── */}
        <VegaCard className="password-reset-card">
          <h2 className="v-font-h5">Step 1 — Request Verification Code</h2>
          <VegaForm ref={requestFormRef}>
            <VegaFlex direction="col" gap="size-16">
              <VegaInput
                label="Email Address"
                placeholder="Enter your registered email"
                data-vega-form="email"
                type="email"
                required
                validationRules={[emailValidationRule]}
              />
              <VegaFlex justifyContent="end">
                <VegaButton
                  label={requestLoading ? 'Sending…' : 'Send Code'}
                  onVegaClick={handleRequestCode}
                  disabled={requestLoading}
                />
              </VegaFlex>
            </VegaFlex>
          </VegaForm>
          {codeSent && (
            <p className="password-reset-success-hint">
              A verification code has been sent to <strong>{requestEmail}</strong>. Check your email inbox.
            </p>
          )}
        </VegaCard>

        {/* ── Reset Password Section ── */}
        <VegaCard className="password-reset-card">
          <h2 className="v-font-h5">Step 2 — Reset Password</h2>

          {tempPassword ? (
            <div className="password-reset-result">
              <p className="password-reset-result-title">Your temporary password:</p>
              <code className="password-reset-temp-pw">{tempPassword}</code>
              <p className="password-reset-result-warning">
                <strong>Important:</strong> Copy this password now. Use it to sign in — you will be required to change it immediately upon first login. This password will not be shown again.
              </p>
              <VegaFlex justifyContent="center" margin={{ top: 'size-16' }}>
                <VegaButton
                  label="Copy Temporary Password"
                  variant="secondary"
                  onVegaClick={handleCopyTempPassword}
                />
              </VegaFlex>
              <VegaFlex justifyContent="center" margin={{ top: 'size-16' }}>
                <VegaButton
                  label="Back to Sign In"
                  onVegaClick={() => {
                    window.location.href = '/';
                  }}
                />
              </VegaFlex>
            </div>
          ) : (
            <VegaForm ref={resetFormRef}>
              <VegaFlex direction="col" gap="size-16">
                <VegaInput
                  label="Email Address"
                  placeholder="Enter your registered email"
                  data-vega-form="resetEmail"
                  type="email"
                  required
                  value={requestEmail}
                  validationRules={[emailValidationRule]}
                />
                <VegaInput
                  label="Verification Code"
                  placeholder="Enter the code from your email"
                  data-vega-form="code"
                  type="text"
                  required
                  validationRules={[codeValidationRule]}
                />
                <VegaFlex justifyContent="end">
                  <VegaButton
                    label={resetLoading ? 'Resetting…' : 'Reset Password'}
                    onVegaClick={handleReset}
                    disabled={resetLoading}
                  />
                </VegaFlex>
              </VegaFlex>
            </VegaForm>
          )}
        </VegaCard>

        {/* ── Back link ── */}
        {!tempPassword && (
          <div
            className="v-text-link cursor-pointer"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Back to Sign In
          </div>
        )}
      </VegaFlex>
    </div>
  );
};

export default PasswordReset;
